import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';

export default function CareerPage() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('positions');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    education: '',
    city: '',
    district: '',
    address: '',
    message: '',
    cvFile: null as File | null
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setFormStatus('error');
        setFormMessage('CV dosyası 5MB\'dan küçük olmalıdır.');
        setTimeout(() => {
          setFormStatus('idle');
          setFormMessage('');
        }, 3000);
        return;
      }
      setFormData(prev => ({
        ...prev,
        cvFile: file
      }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim() || 
        !formData.position || !formData.experience || 
        !formData.city.trim() || !formData.district.trim() || !formData.address.trim() || 
        !formData.message.trim()) {
      setFormStatus('error');
      setFormMessage('Lütfen tüm zorunlu alanları doldurun.');
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 3000);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus('error');
      setFormMessage('Geçerli bir e-posta adresi girin.');
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 3000);
      return;
    }

    // Message length validation
    if (formData.message.length > 500) {
      setFormStatus('error');
      setFormMessage('Mesajınız 500 karakterden uzun olamaz.');
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 3000);
      return;
    }

    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('position', formData.position);
      formBody.append('experience', formData.experience);
      formBody.append('education', formData.education);
      formBody.append('city', formData.city);
      formBody.append('district', formData.district);
      formBody.append('address', formData.address);
      formBody.append('message', formData.message);
      formBody.append('cvFile', formData.cvFile ? 'Toplanamaz' : 'Yüklenmedi');

      const response = await fetch('https://readdy.ai/api/form/d54d27abfdatfusc4d2g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormMessage('Başvurunuz başarıyla gönderildi! En kısa sürede size dönüş yapacağız.');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          education: '',
          city: '',
          district: '',
          address: '',
          message: '',
          cvFile: null
        });
        // Reset file input
        const fileInput = document.getElementById('cvFile') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        
        setTimeout(() => {
          setFormStatus('idle');
          setFormMessage('');
        }, 5000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setFormStatus('error');
      setFormMessage('Bir hata oluştu. Lütfen tekrar deneyin.');
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 3000);
    }
  };

  const benefits = [
    {
      icon: 'ri-team-line',
      title: 'Dinamik Ekip',
      description: 'Genç ve dinamik ekibimizle birlikte çalışma fırsatı'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Sürekli Gelişim',
      description: 'Profesyonel gelişim için eğitim ve sertifikasyon desteği'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Kariyer Fırsatları',
      description: 'Hızlı büyüyen şirketimizde kariyer yapma imkanı'
    },
    {
      icon: 'ri-heart-line',
      title: 'Çalışan Memnuniyeti',
      description: 'Esnek çalışma saatleri ve çalışan dostu politikalar'
    }
  ];

  const openPositions = [
    {
      title: 'Kalite Kontrol Uzmanı',
      department: 'Kalite Kontrol',
      location: 'Kocaeli',
      type: 'Tam Zamanlı',
      description: 'Otomotiv yan sanayinde kalite kontrol süreçlerini yönetecek deneyimli uzman arıyoruz.'
    },
    {
      title: 'İnsan Kaynakları Uzmanı',
      department: 'İnsan Kaynakları',
      location: 'İstanbul',
      type: 'Tam Zamanlı',
      description: 'İşe alım, performans yönetimi ve çalışan gelişimi süreçlerinde görev alacak İK uzmanı arıyoruz.'
    },
    {
      title: 'Kurumsal Eğitmen',
      department: 'Eğitim',
      location: 'Hibrit',
      type: 'Yarı Zamanlı',
      description: 'Liderlik, iletişim ve mesleki gelişim eğitimleri verecek deneyimli eğitmen arıyoruz.'
    }
  ];

  const cultureValues = [
    {
      icon: 'ri-lightbulb-line',
      title: 'İnovasyon',
      description: 'Sürekli yenilik ve gelişim odaklı çalışma kültürü'
    },
    {
      icon: 'ri-team-line',
      title: 'İşbirliği',
      description: 'Takım çalışması ve birlikte başarı anlayışı'
    },
    {
      icon: 'ri-star-line',
      title: 'Mükemmellik',
      description: 'Her işte en yüksek kalite standardını hedefleme'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Güvenilirlik',
      description: 'Dürüstlük ve şeffaflık ilkesiyle çalışma'
    }
  ];

  const benefitsList = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'Sağlık Sigortası',
      description: 'Kapsamlı özel sağlık sigortası'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Esnek Çalışma',
      description: 'Hibrit ve esnek çalışma modelleri'
    },
    {
      icon: 'ri-book-open-line',
      title: 'Eğitim Desteği',
      description: 'Mesleki gelişim eğitimleri ve sertifikalar'
    },
    {
      icon: 'ri-gift-line',
      title: 'Performans Primi',
      description: 'Başarı odaklı ödül sistemi'
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Yemek Kartı',
      description: 'Aylık yemek kartı desteği'
    },
    {
      icon: 'ri-car-line',
      title: 'Ulaşım Desteği',
      description: 'Servis veya ulaşım yardımı'
    }
  ];

  const applicationProcess = [
    {
      step: '1',
      title: 'Başvuru',
      description: 'Online formu doldurun ve CV\'nizi yükleyin'
    },
    {
      step: '2',
      title: 'İnceleme',
      description: 'İK ekibimiz başvurunuzu değerlendirir'
    },
    {
      step: '3',
      title: 'Görüşme',
      description: 'Uygun adaylarla mülakat gerçekleştirilir'
    },
    {
      step: '4',
      title: 'Değerlendirme',
      description: 'Teknik ve yetkinlik değerlendirmesi yapılır'
    },
    {
      step: '5',
      title: 'Teklif',
      description: 'Başarılı adaylara iş teklifi sunulur'
    },
    {
      step: '6',
      title: 'İşe Başlama',
      description: 'Oryantasyon programı ile işe başlama'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header>
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-12 w-12 object-contain" />
                <h1 className="text-xl font-bold text-gray-900">AXIOM</h1>
              </Link>
              
              {/* Desktop Menu */}
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_home')}</Link>
                <Link to="/about" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_about')}</Link>
                <Link to="/services" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_services')}</Link>
                <Link to="/training" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_training')}</Link>
                <Link to="/blog" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_blog')}</Link>
                <Link to="/career" className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors">Kariyer</Link>
                <Link to="/contact" className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                <LanguageSwitcher />
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
                <div className="flex flex-col gap-2">
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_home')}</Link>
                  <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_about')}</Link>
                  <Link to="/services" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_services')}</Link>
                  <Link to="/training" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_training')}</Link>
                  <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_blog')}</Link>
                  <Link to="/career" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">Kariyer</Link>
                  <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                  <div className="px-3 py-2">
                    <LanguageSwitcher />
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-teal-100 rounded-full px-4 py-2 mb-6">
                <i className="ri-briefcase-line text-teal-600 text-sm"></i>
                <span className="text-teal-700 text-sm font-semibold">Kariyer Fırsatları</span>
              </div>
              <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                Bizimle Çalışmak<br />İster misiniz?
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                AXIOM ailesine katılın! Kalite kontrol ve insan kaynakları alanında kariyer yapma fırsatını yakalayın. Dinamik ekibimizle birlikte büyüyün ve gelişin.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 border border-gray-100">
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${benefit.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tabs Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            {/* Tab Navigation */}
            <div className="flex flex-wrap justify-center gap-4 mb-16">
              <button
                onClick={() => setActiveTab('positions')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'positions'
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Açık Pozisyonlar
              </button>
              <button
                onClick={() => setActiveTab('culture')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'culture'
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Şirket Kültürü
              </button>
              <button
                onClick={() => setActiveTab('benefits')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'benefits'
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Yan Haklar
              </button>
              <button
                onClick={() => setActiveTab('process')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'process'
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Başvuru Süreci
              </button>
            </div>

            {/* Tab Content */}
            <div className="min-h-[500px]">
              {/* Açık Pozisyonlar Tab */}
              {activeTab === 'positions' && (
                <div className="animate-fadeIn">
                  <div className="mb-12 text-center">
                    <h2 className="text-5xl font-bold text-gray-900 mb-4">Açık Pozisyonlar</h2>
                    <p className="text-lg text-gray-600">Ekibimize katılmak için aşağıdaki pozisyonlara başvurabilirsiniz</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {openPositions.map((position, index) => (
                      <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-all hover:-translate-y-1">
                        <div className="flex items-start justify-between mb-4">
                          <span className="bg-teal-100 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">{position.type}</span>
                          <i className="ri-map-pin-line text-gray-400"></i>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{position.title}</h3>
                        <div className="flex items-center gap-4 mb-4">
                          <span className="text-sm text-gray-600">{position.department}</span>
                          <span className="text-gray-300">•</span>
                          <span className="text-sm text-gray-600">{position.location}</span>
                        </div>
                        <p className="text-gray-600 mb-6 leading-relaxed">{position.description}</p>
                        <a href="#basvuru" className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer">
                          <span>Başvur</span>
                          <i className="ri-arrow-right-line"></i>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Şirket Kültürü Tab */}
              {activeTab === 'culture' && (
                <div className="animate-fadeIn">
                  <div className="mb-12 text-center">
                    <h2 className="text-5xl font-bold text-gray-900 mb-4">Şirket kültürümüz</h2>
                    <p className="text-lg text-gray-600">AXIOM'da çalışmanın temel değerleri</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                    {cultureValues.map((value, index) => (
                      <div key={index} className="text-center">
                        <div className="w-20 h-20 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                          <i className={`${value.icon} text-4xl text-teal-600`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                        <p className="text-gray-600">{value.description}</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-12">
                    <div className="max-w-3xl mx-auto text-center">
                      <h3 className="text-3xl font-bold text-gray-900 mb-6">Çalışan Deneyimi</h3>
                      <p className="text-lg text-gray-700 leading-relaxed mb-8">
                        AXIOM'da her çalışan değerlidir. Açık iletişim, sürekli gelişim ve takım ruhu ile çalışanlarımızın potansiyellerini en üst düzeye çıkarmalarını destekliyoruz. İş-yaşam dengesi, esnek çalışma modelleri ve kişisel gelişim fırsatları ile mutlu bir çalışma ortamı sunuyoruz.
                      </p>
                      <div className="grid grid-cols-3 gap-8">
                        <div>
                          <div className="text-4xl font-bold text-teal-600 mb-2">95%</div>
                          <div className="text-sm text-gray-600">Çalışan Memnuniyeti</div>
                        </div>
                        <div>
                          <div className="text-4xl font-bold text-teal-600 mb-2">4.8</div>
                          <div className="text-sm text-gray-600">Glassdoor Puanı</div>
                        </div>
                        <div>
                          <div className="text-4xl font-bold text-teal-600 mb-2">3+</div>
                          <div className="text-sm text-gray-600">Ortalama Çalışma Süresi</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Yan Haklar Tab */}
              {activeTab === 'benefits' && (
                <div className="animate-fadeIn">
                  <div className="mb-12 text-center">
                    <h2 className="text-5xl font-bold text-gray-900 mb-4">Yan Haklar ve Avantajlar</h2>
                    <p className="text-lg text-gray-600">Çalışanlarımıza sunduğumuz imkanlar</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {benefitsList.map((benefit, index) => (
                      <div key={index} className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all">
                        <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                          <i className={`${benefit.icon} text-3xl text-teal-600`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.description}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-16 bg-gradient-to-r from-teal-600 to-emerald-600 rounded-3xl p-12 text-white text-center">
                    <h3 className="text-3xl font-bold mb-4">Ve Daha Fazlası!</h3>
                    <p className="text-lg opacity-90 max-w-2xl mx-auto">
                      Çalışanlarımızın mutluluğu ve gelişimi bizim için önceliklidir. Sürekli iyileştirdiğimiz yan haklar ve avantajlarımızla yanınızdayız.
                    </p>
                  </div>
                </div>
              )}

              {/* Başvuru Süreci Tab */}
              {activeTab === 'process' && (
                <div className="animate-fadeIn">
                  <div className="mb-12 text-center">
                    <h2 className="text-5xl font-bold text-gray-900 mb-4">Başvuru Süreci</h2>
                    <p className="text-lg text-gray-600">İşe alım sürecimiz 6 adımdan oluşur</p>
                  </div>

                  <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {applicationProcess.map((step, index) => (
                        <div key={index} className="relative">
                          <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-teal-600 transition-all h-full">
                            <div className="w-16 h-16 bg-teal-600 text-white rounded-xl flex items-center justify-center text-2xl font-bold mb-6">
                              {step.step}
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                            <p className="text-gray-600">{step.description}</p>
                          </div>
                          {index < applicationProcess.length - 1 && (
                            <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                              <i className="ri-arrow-right-line text-2xl text-teal-600"></i>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="mt-16 bg-teal-50 rounded-3xl p-8 text-center">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Ortalama Süreç Süresi</h3>
                      <div className="text-5xl font-bold text-teal-600 mb-2">2-3 Hafta</div>
                      <p className="text-gray-600">Başvurudan işe başlamaya kadar geçen ortalama süre</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Application Form Section */}
        <section id="basvuru" className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">İş Başvurusu Yapın</h2>
              <p className="text-lg text-gray-600">Formu doldurun, CV'nizi yükleyin ve ekibimize katılın</p>
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
              <form 
                id="career-application-form" 
                data-readdy-form 
                className="space-y-6" 
                onSubmit={handleFormSubmit}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Ad Soyad</label>
                    <input 
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">E-posta</label>
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                      placeholder="ornek@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Telefon</label>
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                      placeholder="+90 (5__) ___ __ __"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Başvurulan Pozisyon</label>
                    <select
                      name="position"
                      value={formData.position}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">Pozisyon seçin</option>
                      <option value="Kalite Kontrol Uzmanı">Kalite Kontrol Uzmanı</option>
                      <option value="İnsan Kaynakları Uzmanı">İnsan Kaynakları Uzmanı</option>
                      <option value="Kurumsal Eğitmen">Kurumsal Eğitmen</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Deneyim</label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">Deneyim seçin</option>
                      <option value="0-1 yıl">0-1 yıl</option>
                      <option value="1-3 yıl">1-3 yıl</option>
                      <option value="3-5 yıl">3-5 yıl</option>
                      <option value="5-10 yıl">5-10 yıl</option>
                      <option value="10+ yıl">10+ yıl</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Eğitim Durumu</label>
                    <select
                      name="education"
                      value={formData.education}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">Eğitim durumu seçin</option>
                      <option value="Lise">Lise</option>
                      <option value="Ön Lisans">Ön Lisans</option>
                      <option value="Lisans">Lisans</option>
                      <option value="Yüksek Lisans">Yüksek Lisans</option>
                      <option value="Doktora">Doktora</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">İl</label>
                    <input 
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                      placeholder="İl"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">İlçe</label>
                    <input 
                      type="text"
                      name="district"
                      value={formData.district}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                      placeholder="İlçe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Adres</label>
                  <input 
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                    placeholder="Tam adresiniz"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">CV Yükle</label>
                  <div className="relative">
                    <input 
                      type="file"
                      id="cvFile"
                      name="cvFile"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">PDF, DOC veya DOCX formatında, maksimum 5MB</p>
                  {formData.cvFile && (
                    <p className="text-sm text-teal-600 mt-2 flex items-center gap-2">
                      <i className="ri-file-text-line"></i>
                      <span>{formData.cvFile.name}</span>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Kendinizden Bahsedin</label>
                  <textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all resize-none text-sm"
                    placeholder="Neden bu pozisyon için uygun olduğunuzu ve kariyer hedeflerinizi kısaca anlatın..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 karakter</p>
                </div>

                {formMessage && (
                  <div className={`p-4 rounded-lg ${
                    formStatus === 'success' 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    <p className="text-sm">{formMessage}</p>
                  </div>
                )}

                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className={`w-full py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    formStatus === 'submitting'
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {formStatus === 'submitting' ? 'Gönderiliyor...' : 'Başvuruyu Gönder'}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Why Join Us Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="rounded-3xl overflow-hidden h-[500px]">
                <img 
                  src="https://public.readdy.ai/ai/img_res/ee52d20a002daccacd75dad4667290ef.jpg" 
                  alt="AXIOM Ekibi" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <h2 className="text-5xl font-bold text-gray-900 mb-6">Neden AXIOM?</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  AXIOM'da çalışmak, sadece bir iş değil, aynı zamanda kariyer yolculuğunuzda büyüme ve gelişim fırsatıdır. Yenilikçi yaklaşımımız, destekleyici ekip kültürümüz ve sürekli öğrenme ortamımızla yanınızdayız.
İnsan ve üretimde güvenin adı AXIOM.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Profesyonel Gelişim</h4>
                      <p className="text-sm text-gray-600">Eğitim programları ve sertifikasyon destekleri ile sürekli gelişim</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Esnek Çalışma</h4>
                      <p className="text-sm text-gray-600">İş-yaşam dengesini destekleyen esnek çalışma modelleri</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Kariyer Fırsatları</h4>
                      <p className="text-sm text-gray-600">Hızlı büyüyen şirketimizde kariyer basamaklarını hızla tırmanma imkanı</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Takım Ruhu</h4>
                      <p className="text-sm text-gray-600">Birlikte başarıya ulaşan, destekleyici ve samimi ekip ortamı</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-10 w-10 object-contain" />
                <span className="text-2xl font-bold">AXIOM</span>
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                {t('footer_description')}
              </p>
              <div className="flex gap-4 mb-6">
                <a href="https://www.linkedin.com/in/axiom-kalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="https://x.com/AxiomKalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a href="https://www.instagram.com/axiomkalite/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
                <a href="https://www.facebook.com/profile.php?id=61585433974202" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-teal-600 text-lg mt-1"></i>
                <div>
                  <p className="text-gray-400 text-sm leading-relaxed mb-2">
                    Maslak Mahallesi, Büyükdere Caddesi<br />İstanbul
                  </p>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Maslak+Mahallesi+Büyükdere+Caddesi+İstanbul" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors text-sm font-medium cursor-pointer"
                  >
                    <i className="ri-navigation-line"></i>
                    <span>Yol Tarifi Al</span>
                  </a>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_services')}</h4>
              <ul className="space-y-3">
                <li><Link to="/#hizmetler" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_quality')}</Link></li>
                <li><Link to="/#hizmetler" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_hr')}</Link></li>
                <li><Link to="/#egitimler" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_training')}</Link></li>
                <li><Link to="/#hizmetler" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_audit')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_corporate')}</h4>
              <ul className="space-y-3">
                <li><Link to="/#hakkimizda" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_about')}</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_team')}</a></li>
                <li><Link to="/career" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_career')}</Link></li>
                <li><Link to="/#iletisim" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_contact')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_resources')}</h4>
              <ul className="space-y-3">
                <li><Link to="/#blog" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_blog')}</Link></li>
                <li><a href="#referanslar" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_references')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_faq')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_documents')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
              <p>{t('footer_copyright')}</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                {t('footer_powered')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}